import {
  CategoryFilter,
  categoryFilterOrder,
  CategoryKey,
  categoryLabels,
  categoryOrder,
  ExperienceCreationCard,
} from "@src/components/Dialogs/CreateExperienceDialog/types";
import { ReactNode, useRef } from "react";
import { Box, IconButton, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { X } from "lucide-react";
import { ExperienceTypeCard } from "@src/components/Dialogs/CreateExperienceDialog/ExperienceTypeCard";
import { hasCardForCategory } from "@src/components/Dialogs/CreateExperienceDialog/helpers";

interface TypeSelectionPageProps {
  activeCategory: CategoryFilter;
  visibleCards: ExperienceCreationCard[];
  getDisabledReason: (card: ExperienceCreationCard) => ReactNode;
  getOutOfScopeTier: (
    card: ExperienceCreationCard
  ) => "Core" | "Plus" | undefined;
  onCategoryChange: (category: CategoryFilter) => void;
  onClose: () => void;
  onSelect: (card: ExperienceCreationCard) => void;
}
export const TypeSelectionPage = ({
  activeCategory,
  visibleCards,
  getDisabledReason,
  getOutOfScopeTier,
  onCategoryChange,
  onClose,
  onSelect,
}: TypeSelectionPageProps) => {
  const theme = useTheme();
  const sectionRefs = useRef<
    Partial<Record<CategoryKey, HTMLDivElement | null>>
  >({});
  const availableCategories = categoryOrder.filter((category) =>
    hasCardForCategory(category, visibleCards)
  );
  const sectionCategories = categoryOrder.filter((category) =>
    hasCardForCategory(category, visibleCards)
  );

  const handleCategoryChange = (category: CategoryFilter) => {
    onCategoryChange(category);
  };

  return (
    <Stack spacing={6} sx={{ p: 6, height: "670px" }}>
      <Stack spacing={4}>
        <Grid container alignItems="center" wrap="nowrap">
          <Grid flex={1} />
          <Grid flex={4} textAlign="center">
            <IgTypography variant="h2" fontWeight={600}>
              Choose an Experience to create
            </IgTypography>
          </Grid>
          <Grid flex={1} textAlign="right">
            <IconButton onClick={onClose} size="small">
              <X size={24} />
            </IconButton>
          </Grid>
        </Grid>
        <Stack
          direction="row"
          spacing={1}
          alignSelf="center"
          sx={{
            border: `1px solid ${theme.igBorders.core.default}`,
            borderRadius: "6px",
            p: 1,
          }}
        >
          {categoryFilterOrder
            .filter(
              (category) =>
                category === "all" ||
                availableCategories.includes(category as CategoryKey)
            )
            .map((category) => {
              const isActive = activeCategory === category;
              return (
                <IgButton
                  key={category}
                  size="small"
                  variant={isActive ? "contained" : "text"}
                  color={isActive ? "secondary" : "secondary"}
                  onClick={() => handleCategoryChange(category)}
                  sx={{ minWidth: "auto", height: 26, px: 3 }}
                >
                  {categoryLabels[category]}
                </IgButton>
              );
            })}
        </Stack>
      </Stack>
      <Stack
        spacing={8}
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          pr: 1,
        }}
      >
        {sectionCategories
          .filter((section) =>
            activeCategory === "all" ? section : section === activeCategory
          )
          .map((category) => (
            <Stack
              key={category}
              ref={(element) => {
                sectionRefs.current[category] = element;
              }}
              spacing={3}
            >
              {activeCategory === "all" && (
                <IgTypography
                  variant="body2"
                  color={theme.palette.text.placeholder}
                  sx={{ textTransform: "uppercase" }}
                  fontWeight={500}
                >
                  {categoryLabels[category]}
                </IgTypography>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 260px)",
                  gap: "12px",
                }}
              >
                {visibleCards
                  .filter((card) => card.category === category)
                  .map((card) => (
                    <ExperienceTypeCard
                      key={card.id}
                      card={card}
                      disabledReason={getDisabledReason(card)}
                      onSelect={onSelect}
                    />
                  ))}
              </Box>
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
};
